import {
  generatePDF,
  generatePDFTable,
  generatePDFTextInBox,
  imageToBase64,
} from '../../../helper/pdfGenerator';
import {
  PDFPage,
  PDFResult,
  PDFTableItem,
  PDFText,
} from '../../../models/pdfModal';

export interface PrincipalRiskDetails {
  title: string;
  probabilityMarks: number;
  impactMarks: number;
}

export const generatePrincipalRisk = async (
  details: (PrincipalRiskDetails | null)[]
): Promise<PDFResult> => {
  const pages: PDFPage[] = [
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...impactVsProbabilityIntroduction(),
        ...impactVsProbabilityTable(details),
        ...impactVsProbabilityLegend(),
        ...impactVsProbabilityKey(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...liquidityIntroduction(),
        ...liquidityTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...fundingIntroduction(),
        ...fundingTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...interestRateIntroduction(),
        ...interestRateTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...foreignExchangeIntroduction(),
        ...foreignExchangeTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...commodityIntroduction(),
        ...commodityTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...creditIntroduction(),
        ...creditTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...operationalIntroduction(),
        ...operationalTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...disasterIntroduction(),
        ...disasterTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...complianceIntroduction(),
        ...complianceTable(),
      ],
    },
    {
      background: '',
      texts: [
        ...(await principalRiskHeader()),
        ...legalIntroduction(),
        ...legalTable(),
      ],
    },
  ];

  return await generatePDF('Principal Risk Report', pages);
};

const principalRiskHeader = async (): Promise<PDFText[]> => {
  const address: string[] = [
    'Suite 8.03, Level 8, Menara AIA,',
    'Intan Milenium Square,',
    'No.68 Jalan Batai Laut 4,',
    'Taman Intan, 41300, Klang',
    'Selangor Darul Ehsan, Malaysia.',
  ];

  const allTexts: PDFText[] = [
    {
      id: '',
      value: '',
      x: 0,
      y: 19,
      box: {
        width: 16,
        height: 6.5,
        color: '#f8d24a',
        strokeColor: null,
      },
    },
    {
      id: '',
      value: await imageToBase64('/report/riskManagementLogo.png'),
      x: 36.5,
      y: 18,
      imageObject: {
        imageHeight: 69,
        imageWidth: 181,
        maxHeight: 11,
        maxWidth: 29,
      },
    },
    {
      id: '',
      value: 'Chartered Accounting Firm',
      x: 37.5,
      y: 21.5,
      fontsize: 5,
      textAlign: 'center',
    },
    ...address.map((add, ind): PDFText => {
      return {
        id: '',
        value: add,
        x: 37.5,
        y: 25 + ind * 2,
        fontsize: 5,
        textAlign: 'center',
      };
    }),
    {
      id: '',
      value: 'HP Tel: +6013-210 0283 Tel: +603-3341 7069',
      x: 37.5,
      y: 36.5,
      fontsize: 5,
      textAlign: 'center',
    },
    {
      id: '',
      value: '',
      x: 59,
      y: 19,
      box: {
        width: 151,
        height: 6.5,
        color: '#314c9f',
        strokeColor: null,
      },
    },
    {
      id: '',
      value: 'Private & Confidential',
      x: 184.5,
      y: 17,
      textAlign: 'right',
      fontStyle: 'bolditalic',
      fontsize: 12,
    },
    {
      id: '',
      value: 'REPORTING ON PRINCIPAL RISKS',
      x: 184.5,
      y: 32,
      textAlign: 'right',
      fontStyle: 'bold',
      maxWidth: 104,
      fontsize: 16,
      fontColor: '#2f5496',
    },
  ];

  return allTexts;
};

const generatePrincipalRiskTableData = (
  items: string[][],
  fontsize: number = 11
): PDFTableItem[][] => {
  return items.map((r, rInd) => {
    return r.map((c, cInd) => {
      if (rInd == 0) {
        return {
          value: c,
          fill: '',
          fontSize: fontsize,
          fontStyle: 'bold',
          textAlign: 'center',
          vAlign: 'middle',
        };
      } else if (cInd == 0) {
        return {
          value: c,
          fontStyle: 'bold',
          fill: '',
          fontSize: fontsize,
        };
      } else {
        return {
          value: c,
          fill: '',
          fontSize: fontsize,
        };
      }
    });
  });
};

// #region First page
const impactVsProbabilityIntroduction = (): PDFText[] => {
  return [
    {
      id: 'introTitle',
      value: 'Introduction',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'introDesc',
      value:
        'Risk management is the systematic process of identifying, assessing, and mitigating threats or uncertainties that can affect your organization. It involves analyzing risks’ likelihood and impact, developing strategies to minimize harm, and monitoring measures’ effectiveness.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'trendTitle',
      value: 'Summary of Principal Risks and Risk Trend',
      x: 25,
      y: 74,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'trendDesc',
      value:
        'The risk heat map below shows the likelihood and potential impact of principal risks to the Company, with key mitigation actions outlined on the next page.',
      x: 25,
      y: 82,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const impactVsProbabilityTable = (
  details: (PrincipalRiskDetails | null)[]
): PDFText[] => {
  const allRows: PDFTableItem[][] = [
    [
      {
        value: '5',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'middle',
      },
      { value: '5', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '10', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '15', fill: '#ffc000', textAlign: 'center', vAlign: 'middle' },
      { value: '20', fill: '#ffc000', textAlign: 'center', vAlign: 'middle' },
      { value: '25', fill: '#ff0000', textAlign: 'center', vAlign: 'middle' },
    ],
    [
      { value: '4', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '4', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '8', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '12', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '16', fill: '#ffc000', textAlign: 'center', vAlign: 'middle' },
      { value: '20', fill: '#ffc000', textAlign: 'center', vAlign: 'middle' },
    ],
    [
      { value: '3', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '3', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '6', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '9', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '12', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '15', fill: '#ffc000', textAlign: 'center', vAlign: 'middle' },
    ],
    [
      { value: '2', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '2', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '4', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '6', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '8', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
      { value: '10', fill: '#ffff00', textAlign: 'center', vAlign: 'middle' },
    ],
    [
      { value: '1', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '1', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '2', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '3', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '4', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
      { value: '5', fill: '#00b050', textAlign: 'center', vAlign: 'middle' },
    ],
    [
      { value: '', fill: '', textAlign: 'center', vAlign: 'middle' },
      { value: '1', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '2', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '3', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '4', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
      { value: '5', fill: '#d9d9d9', textAlign: 'center', vAlign: 'middle' },
    ],
  ];

  const xAxisName: PDFText = {
    id: 'xaxis',
    value: 'PROBABILITY',
    x: 92,
    y: 219,
    fontStyle: 'bold',
    maxWidth: 99,
    textAlign: 'center',
    fontsize: 18,
  };
  const yAxisName: PDFText = {
    id: 'yaxis',
    value: 'IMPACT',
    x: 48,
    y: 165,
    fontStyle: 'bold',
    maxWidth: 108,
    textAlign: 'center',
    fontsize: 18,
    textRotate: 90,
  };

  const boxSize = 5;
  const fontSize = 7;
  const startX = 59 - boxSize / 2;
  const startY = 190 - boxSize / 2;
  const boxWidth = 16.5;
  const boxHeight = 18;

  console.log(details);
  const boxes = details
    .filter((p) => p != null)
    .map((p) => {
      const liquidationProbability =
        startX + (p?.probabilityMarks || 0) * boxWidth;
      const liquidationImpact = startY - (p?.impactMarks || 0) * boxHeight;

      return generatePDFTextInBox(
        {
          id: '',
          value: p?.title || '',
          x: liquidationProbability,
          y: liquidationImpact,
          textAlign: 'center',
          fontsize: fontSize,
          box: {
            height: boxSize,
            width: boxSize,
            color: '#FFF',
            strokeColor: '#000',
          },
        },
        0,
        'middle'
      );
    });

  return [
    xAxisName,
    yAxisName,
    ...generatePDFTable(
      allRows,
      [18, 18, 18, 18, 18, 18],
      [16.5, 16.5, 16.5, 16.5, 16.5, 16.5],
      42.5,
      100
    ),
    ...boxes.flat(),
  ];
};
const impactVsProbabilityLegend = (): PDFText[] => {
  const legendTableItems: PDFTableItem[][] = [
    [
      {
        value: '1 - 5',
        fill: '',
        vAlign: 'bottom',
      },
      {
        value: 'Green',
        fill: '#00af50',
        vAlign: 'bottom',
        textAlign: 'center',
      },
    ],
    [
      {
        value: '6 - 12',
        fill: '',
        vAlign: 'bottom',
      },
      {
        value: 'Yellow',
        fill: '#ffff00',
        vAlign: 'bottom',
        textAlign: 'center',
      },
    ],
    [
      {
        value: '13 - 20',
        fill: '',
        vAlign: 'bottom',
      },
      {
        value: 'Orange',
        fill: '#ffc000',
        vAlign: 'bottom',
        textAlign: 'center',
      },
    ],
    [
      {
        value: '21 - 25',
        fill: '',
        vAlign: 'bottom',
      },
      {
        value: 'Red',
        fill: '#fe0000',
        vAlign: 'bottom',
        textAlign: 'center',
      },
    ],
  ];
  const legendTable: PDFText[] = generatePDFTable(
    legendTableItems,
    [10, 10, 10, 10],
    [21, 34],
    150.5,
    113.5
  );

  return [
    ...generatePDFTextInBox(
      {
        id: 'legendTitle',
        value: 'Risk Impact vs Probability',
        x: 150.5,
        y: 103,
        box: {
          height: 10.5,
          width: 55,
          color: '#d9d9d9',
          strokeColor: '#000000',
        },
      },
      1,
      'middle'
    ),
    ...legendTable,
  ];
};
const impactVsProbabilityKey = (): PDFText[] => {
  const keyTitle: PDFText = {
    id: 'keyTitle',
    value: 'Key',
    fontStyle: 'bold',
    x: 27,
    y: 235,
  };
  const keys1: PDFTableItem[][] = [
    [
      {
        value: 'Impact',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '2',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '3',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '4',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '5',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
    ],
    [
      {
        value: '',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Negligible',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Low',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Medium',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'High',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Very High',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
    ],
  ];
  const keys2: PDFTableItem[][] = [
    [
      {
        value: 'Probability',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: '1',
        fill: '#d9d9d9',
        textAlign: 'center',
        vAlign: 'bottom',
      },
    ],
    [
      {
        value: '',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Improbable',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Remote',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Fairly Likely',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Probable',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
      {
        value: 'Very Likely',
        fill: '',
        textAlign: 'center',
        vAlign: 'bottom',
      },
    ],
  ];

  return [
    keyTitle,
    ...generatePDFTable(
      keys1,
      [10, 10],
      [32.5, 24.5, 24.5, 24.5, 24.5, 24.5],
      25.5,
      236.5
    ),
    ...generatePDFTable(
      keys2,
      [10, 10],
      [32.5, 24.5, 24.5, 24.5, 24.5, 24.5],
      25.5,
      265
    ),
  ];
};
// #endregion First page

// #region Liquidity
const liquidityIntroduction = (): PDFText[] => {
  return [
    {
      id: 'liquidityTitle',
      value: 'Liquidity Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'liquidityDesc',
      value:
        'The risk that an individual or firm will have difficulty selling an asset without incurring a loss. That is, there may be a lack of interest in the market for a particular asset, forcing the owner to sell it for less than its actual value.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const liquidityTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Liquidity Risk', 'Mitigation Actions'],
    [
      'Cash flow shortfall',
      '- Prepare detailed short-, medium-, and long-term cash flow forecasts - Conduct regular stress testing and scenario analysis - Implement contingency funding plans',
    ],
    [
      'Over-reliance on short-term funding',
      '- Diversify funding sources (banks, capital markets, trade finance) - Secure committed credit lines from financial institutions - Reduce dependency on short-term borrowings',
    ],
    [
      'Mismatch between asset & liability maturities',
      '- Perform maturity gap analysis - Match asset and liability tenors (ALM) - Limit exposure to illiquid, long-term investments',
    ],
    [
      'Insufficient liquid assets',
      '- Maintain adequate buffer of cash and marketable securities - Set internal liquidity coverage ratio (LCR) limits - Establish policies to monitor and maintain liquidity buffers',
    ],
    [
      'Unexpected market or funding shocks',
      '- Develop and test contingency funding plans - Maintain relationships with multiple financial institutions - Implement early warning indicators for market stress',
    ],
    [
      'Operational and policy weaknesses',
      '- Establish clear liquidity risk management policy - Define roles and responsibilities for liquidity management - Ensure regular board/management oversight and reporting',
    ],
    [
      'Reputation or credit rating downgrade',
      '- Maintain strong financial discipline and creditworthiness - Communicate transparently with lenders, investors, regulators - Optimize capital structure and debt servicing ability',
    ],
    [
      'Regulatory non-compliance',
      '- Monitor liquidity ratios (LCR, NSFR, current ratio) - Align policies with regulatory requirements - Conduct regular audits and compliance checks',
    ],
  ]);

  return generatePDFTable(
    item,
    [6, 16, 16, 16, 16, 16, 16, 16, 16],
    [41, 119],
    25,
    65
  );
};
// #endregion Liquidity

// #region Funding
const fundingIntroduction = (): PDFText[] => {
  return [
    {
      id: 'fundingTitle',
      value: 'Funding Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'fundingDesc',
      value:
        'Funding risk is the possibility that a business or individual might not have access to the financing it needs at an affordable rate.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const fundingTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Funding Risk', 'Mitigation Actions'],
    [
      'Concentration of funding sources',
      '- Diversify funding sources (banks, capital markets, trade finance, equity) - Build relationships with multiple lenders and investors - Set internal limits on single counterparty exposure',
    ],
    [
      'Reliance on short-term or volatile funding',
      '- Extend debt maturity profile (shift to medium-/long-term funding) - Secure committed credit facilities for emergencies - Conduct maturity gap analysis to align assets and liabilities',
    ],
    [
      'High cost of funding',
      '- Maintain strong credit rating and reputation - Optimize capital structure (balance between debt and equity) - Improve financial transparency and investor communication',
    ],
    [
      'Loss of market access (due to shocks or credit downgrade)',
      '- Maintain contingency funding plans - Hold sufficient liquid assets as a funding buffer - Establish alternative funding arrangements (e.g., securitization, sale-leaseback)',
    ],
    [
      'Regulatory and covenant restrictions',
      '- Regularly review and comply with debt covenants - Monitor regulatory liquidity and funding requirements - Engage early with regulators and lenders on potential issues',
    ],
    [
      'Sudden withdrawal of deposits/lines (for banks/NBFIs)',
      '- Monitor counterparty behavior and funding rollovers - Maintain early warning indicators (e.g., withdrawal patterns) - Keep diversified and stable depositor/investor base',
    ],
    [
      'Operational/management weaknesses in funding strategy',
      '- Establish a formal funding risk management policy - Integrate funding strategy into overall ALM framework - Provide regular reports to senior management and board',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 63;

  return generatePDFTable(
    item,
    [6, 20, 20, 20, 20, 20, 20, 20],
    [firstCol, totalWidth - firstCol],
    25,
    60
  );
};
// #endregion Funding

// #region Interest Rate
const interestRateIntroduction = (): PDFText[] => {
  return [
    {
      id: 'interestTitle',
      value: 'Interest Rate Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'interestDesc',
      value:
        'The interest rate risk, or market risk, refers to the chance that investments in bonds will suffer as the result of unexpected interest rate changes.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const interestRateTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Interest Rate Risk', 'Mitigation Actions'],
    [
      'Maturity mismatch between assets and liabilities',
      '- Conduct Asset-Liability Management (ALM) with gap analysis - Match asset and liability durations where possible - Rebalance portfolio to align with interest rate outlook',
    ],
    [
      'Earnings volatility from rate changes',
      '- Use interest rate swaps, caps, collars, or futures to hedge exposure - Diversify asset portfolio (fixed-rate and floating-rate instruments) - Adjust pricing strategies for loans and deposits',
    ],
    [
      'Refinancing/reinvestment risk',
      '- Extend debt maturities to reduce frequent refinancing - Stagger refinancing dates to avoid concentration - Maintain a mix of short-term and long-term instruments',
    ],
    [
      'Market value risk (impact on bond/loan portfolio value)',
      '- Monitor sensitivity through duration and convexity analysis - Limit exposure to long-duration assets in volatile rate environments - Invest in floating-rate or inflation-linked instruments',
    ],
    [
      'Basis risk (mismatch between reference rates)',
      '- Regularly review reference rate exposures (e.g., SOFR, LIBOR, OPR) - Align funding and investment products to the same benchmarks - Use derivatives to hedge basis mismatches',
    ],
    [
      'Embedded options in products (e.g., loan prepayments, deposit withdrawals)',
      '- Incorporate option-adjusted spread (OAS) analysis in pricing - Set limits on optionality exposure - Adjust product terms to reduce sensitivity to prepayment/withdrawals',
    ],
    [
      'Regulatory and compliance risk',
      '- Monitor compliance with interest rate risk regulations (e.g., IRRBB for banks) - Perform regular stress testing under regulatory guidelines - Maintain transparent reporting to regulators and board',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 65;

  return generatePDFTable(
    item,
    [6, 20, 20, 20, 20, 20, 20, 20],
    [firstCol, totalWidth - firstCol],
    25,
    60
  );
};
// #endregion Interest Rate

// #region Foreign Exchange
const foreignExchangeIntroduction = (): PDFText[] => {
  return [
    {
      id: 'foreignExchangeTitle',
      value: 'Foreign Exchange Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'foreignExchangeDesc',
      value:
        'Foreign exchange risk refers to the losses that an international financial transaction may incur due to currency fluctuations.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const foreignExchangeTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Foreign Exchange Risk', 'Mitigation Actions'],
    [
      'Transaction risk (exposure from receivables/payables in foreign currency)',
      '- Use forward contracts, currency swaps, or options to hedge exposure - Invoice and settle in home currency where possible - Netting of receivables and payables in the same currency',
    ],
    [
      'Translation risk (converting foreign subsidiaries’ financials into reporting currency)',
      '- Use natural hedges (e.g., borrow in the same currency as assets) - Apply balance sheet hedging strategies - Maintain diversification across different currencies',
    ],
    [
      'Economic risk (long-term impact of exchange rate movements on competitiveness)',
      '- Diversify production and sourcing across countries - Match revenues and costs in the same currency - Adjust pricing strategies to reflect FX movements',
    ],
    [
      'Settlement risk (default or delay in FX transactions)',
      '- Use trusted and reputable counterparties - Utilize Payment-versus-Payment (PvP) settlement systems like CLS - Set counterparty credit limits and monitor exposures',
    ],
    [
      'Speculative/market volatility risk',
      '- Establish clear FX risk management policy with defined hedging ratios - Set Value-at-Risk (VaR) or stop-loss limits for FX positions - Conduct regular stress testing on FX exposures',
    ],
    [
      'Operational/treasury process risk',
      '- Implement centralized treasury for FX management - Segregate duties for deal execution and settlement - Use treasury management systems for monitoring and reporting',
    ],
    [
      'Regulatory & compliance risk',
      '- Ensure adherence to central bank/FX control regulations - Monitor cross-border transaction requirements - Maintain transparent reporting of FX exposures',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 69;

  return generatePDFTable(
    item,
    [6, 20, 20, 16, 20, 20, 20, 20],
    [firstCol, totalWidth - firstCol],
    25,
    60
  );
};
// #endregion Foreign Exchange

// #region Commodity
const commodityIntroduction = (): PDFText[] => {
  return [
    {
      id: 'commodityTitle',
      value: 'Commodity Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'commodityDesc',
      value:
        'Commodity risk is the potential for changes in commodity prices to result in losses. Commodity prices tend to be volatile and can change rapidly over the short term. Many industries take commodities as a basic input and are highly sensitive to changes in prices.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const commodityTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Commodity Risk', 'Mitigation Actions'],
    [
      'Price volatility risk',
      '- Use derivative instruments (futures, forwards, swaps, options) to hedge exposure - Lock in long-term purchase/sales contracts with fixed prices - Apply dynamic hedging strategies to manage changing market conditions',
    ],
    [
      'Supply chain cost risk (rising input/raw material costs)',
      '- Diversify suppliers and sourcing regions - Negotiate flexible pricing arrangements with suppliers - Build strategic inventory reserves of critical commodities',
    ],
    [
      'Revenue risk (falling commodity selling prices)',
      '- Enter into long-term sales agreements with customers - Diversify product mix and customer base - Adjust pricing models with cost pass-through clauses',
    ],
    [
      'Basis risk (hedge not perfectly matching physical exposure)',
      '- Carefully align hedge contracts with underlying exposures - Use customized OTC contracts when exchange-traded products don’t match - Regularly review hedge effectiveness',
    ],
    [
      'Liquidity risk from margin calls on commodity hedges',
      '- Maintain adequate liquidity buffers to meet margin requirements - Use credit facilities to support hedging activities - Monitor exposure limits on derivatives',
    ],
    [
      'Operational/forecasting risk',
      '- Improve demand and consumption forecasting accuracy - Integrate commodity risk management into corporate planning - Establish a centralized commodity risk management function',
    ],
    [
      'Regulatory and compliance risk',
      '- Stay updated on commodity trading regulations - Ensure proper reporting and documentation of hedging activities - Comply with accounting standards for hedge accounting',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 60;

  return generatePDFTable(
    item,
    [6, 24, 16, 16, 20, 16, 20, 20],
    [firstCol, totalWidth - firstCol],
    25,
    70
  );
};
// #endregion Commodity

// #region Credit
const creditIntroduction = (): PDFText[] => {
  return [
    {
      id: 'creditTitle',
      value: 'Credit Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'creditDesc',
      value:
        'Credit risk, or default risk, is the risk that a financial loss will be incurred if a counterpart to a (derivatives) transaction does not fulfil its financial obligations in a timely manner.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const creditTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Credit Risk', 'Mitigation Actions'],
    [
      'Default risk (borrower fails to repay principal/interest)',
      '- Perform thorough credit assessments (credit scoring, financial analysis) - Set prudent lending/credit approval criteria - Diversify credit portfolio across sectors, geographies, and borrowers',
    ],
    [
      'Concentration risk (overexposure to a single borrower/sector)',
      '- Set exposure limits for single counterparties and industries - Regularly review portfolio diversification - Syndicate or participate loans to spread risk',
    ],
    [
      'Counterparty risk (failure of trading/derivative counterparties)',
      '- Use collateral agreements (CSA) and margin requirements - Conduct counterparty creditworthiness checks - Use central clearinghouses where available',
    ],
    [
      'Collateral risk (collateral value declines)',
      '- Apply conservative collateral valuation and haircuts - Regularly revalue and monitor collateral quality - Diversify collateral types accepted',
    ],
    [
      'Settlement risk (failure to deliver on settlement date)',
      '- Use Delivery-versus-Payment (DvP) and Payment-versus-Payment (PvP) systems - Set counterparty settlement limits - Conduct pre-settlement confirmations',
    ],
    [
      'Country/sovereign risk',
      '- Assess political and economic risks of counterparties’ countries - Set country exposure limits - Purchase political risk insurance where appropriate',
    ],
    [
      'Operational and policy weaknesses',
      '- Establish robust credit risk management policies and governance - Implement early warning systems and watchlists - Conduct regular stress testing and scenario analysis',
    ],
    [
      'Regulatory/compliance risk',
      '- Align credit practices with Basel/IFRS requirements - Maintain adequate provisions for non-performing loans (NPLs) - Ensure transparent reporting and regulatory compliance',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 60;

  return generatePDFTable(
    item,
    [6, 20, 16, 16, 16, 20, 16, 20, 20],
    [firstCol, totalWidth - firstCol],
    25,
    65
  );
};
// #endregion Credit

// #region Operational
const operationalIntroduction = (): PDFText[] => {
  return [
    {
      id: 'operationalTitle',
      value: 'Operational Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'operationalDesc',
      value:
        'Operational risk is the prospect of loss resulting from inadequate or failed procedures, human error, systems or policies.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },

    {
      id: 'operationalItem1Bullet',
      value: '',
      x: 32,
      y: 61.5,
      circle: {
        radius: 1.1,
        fill: '#000000',
      },
    },
    {
      id: 'operationalItem1Title',
      value: 'Human Resources',
      x: 38,
      y: 63,
      fontStyle: 'bold',
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'operationalItem1Desc',
      value: '= Employee errors',
      x: 76.5,
      y: 63,
      fontsize: 12,
      textAlign: 'justify',
    },

    {
      id: 'operationalItem2Bullet',
      value: '',
      x: 32,
      y: 67,
      circle: {
        radius: 1.1,
        fill: '#000000',
      },
    },
    {
      id: 'operationalItem2Title',
      value: 'Assets',
      x: 38,
      y: 68.5,
      fontStyle: 'bold',
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'operationalItem2Desc',
      value: '= System failures',
      x: 53,
      y: 68.5,
      fontsize: 12,
      textAlign: 'justify',
    },

    {
      id: 'operationalItem3Bullet',
      value: '',
      x: 32,
      y: 72.5,
      circle: {
        radius: 1.1,
        fill: '#000000',
      },
    },
    {
      id: 'operationalItem3Title',
      value: 'Governance',
      x: 38,
      y: 74,
      fontStyle: 'bold',
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'operationalItem3Desc',
      value: '= Fraud or other criminal activity',
      x: 64,
      y: 74,
      fontsize: 12,
      textAlign: 'justify',
    },

    {
      id: 'operationalItem4Bullet',
      value: '',
      x: 32,
      y: 78,
      circle: {
        radius: 1.1,
        fill: '#000000',
      },
    },
    {
      id: 'operationalItem4Title',
      value: 'Cash Flows',
      x: 38,
      y: 79.5,
      fontStyle: 'bold',
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'operationalItem4Desc',
      value: '= Any event that disrupts business processes',
      x: 63,
      y: 79.5,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const operationalTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Operational Risk', 'Mitigation Actions'],
    [
      'Process failures (inefficient, outdated, or broken workflows)',
      '- Standardize and document key processes - Implement internal controls and segregation of duties - Regularly review and improve workflows',
    ],
    [
      'Human error / misconduct',
      '- Provide regular staff training and awareness programs - Establish clear roles, responsibilities, and accountability - Implement a code of conduct and whistleblowing mechanisms',
    ],
    [
      'System failures (IT outages, cybersecurity threats)',
      '- Maintain robust IT infrastructure with redundancy and backups - Implement cybersecurity frameworks, firewalls, and encryption - Conduct regular system testing and disaster recovery drills',
    ],
    [
      'Fraud and internal abuse',
      '- Enforce strong internal audit and control systems - Limit employee access based on job responsibilities - Use data analytics to detect unusual patterns or transactions',
    ],
    [
      'External events (natural disasters, pandemics, terrorism)',
      '- Develop business continuity and disaster recovery plans (BCP/DRP) - Purchase insurance coverage for catastrophic risks - Diversify operations and supply chains geographically',
    ],
    [
      'Third-party/vendor risk',
      '- Conduct due diligence before onboarding vendors - Establish strong service-level agreements (SLAs) - Monitor vendor performance and financial health regularly',
    ],
    [
      'Compliance and legal risk',
      '- Stay updated with regulatory requirements - Implement compliance monitoring systems - Provide legal and compliance training to staff',
    ],
    [
      'Reputation risk (from operational failures)',
      '- Establish crisis communication plans - Monitor public and customer feedback channels - Strengthen customer service and complaint handling processes',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 47;

  return generatePDFTable(
    item,
    [6, 16, 16, 16, 16, 16, 16, 16, 16],
    [firstCol, totalWidth - firstCol],
    25,
    85
  );
};
// #endregion Operational

// #region Disaster
const disasterIntroduction = (): PDFText[] => {
  return [
    {
      id: 'disasterTitle',
      value: 'Economic Disaster Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'disasterDesc',
      value:
        'Economic disaster is a sudden, calamitous event that seriously disrupts the functioning of a organisation or individual and causes human, material, and economic or environmental losses that exceed the organisation or individual ability to cope using its own resources.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const disasterTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Economic Disaster Risk', 'Mitigation Actions'],
    [
      'Severe recession / economic downturn',
      '- Build financial resilience with strong cash reserves - Diversify revenue streams across industries and geographies - Implement cost optimization and flexible budgeting',
    ],
    [
      'High inflation / currency devaluation',
      '- Use inflation-linked contracts and products - Hedge against currency depreciation (FX hedging) - Adjust pricing strategies and pass-through mechanisms',
    ],
    [
      'Unemployment / reduced demand',
      '- Maintain agile business models to scale operations up/down - Expand into essential goods/services less sensitive to downturns - Strengthen customer retention and loyalty programs',
    ],
    [
      'Financial market collapse / credit crunch',
      '- Diversify funding sources and maintain committed credit lines - Hold a buffer of liquid assets (safe-haven instruments) - Reduce leverage and monitor debt maturities',
    ],
    [
      'Systemic risk (banking collapse, contagion)',
      '- Regularly conduct stress testing and scenario analysis - Establish contingency funding and recovery plans - Build relationships with regulators and industry networks',
    ],
    [
      'Government policy / regulatory shock',
      '- Monitor macroeconomic and policy developments closely - Engage in regulatory dialogues and compliance planning - Adapt corporate strategy to shifting fiscal/monetary policies',
    ],
    [
      'Geopolitical/economic sanctions risk',
      '- Diversify supply chains and markets - Maintain alternative sourcing and export markets - Conduct geopolitical risk assessments regularly',
    ],
    [
      'Social unrest due to economic collapse',
      '- Strengthen corporate social responsibility (CSR) programs - Enhance workforce engagement and retention strategies - Develop crisis communication and stakeholder management plans',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 57;

  return generatePDFTable(
    item,
    [6, 20, 16, 20, 20, 16, 20, 16, 20],
    [firstCol, totalWidth - firstCol],
    25,
    70
  );
};
// #endregion Disaster

// #region Compliance
const complianceIntroduction = (): PDFText[] => {
  return [
    {
      id: 'complianceTitle',
      value: 'Compliance Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'complianceDesc',
      value:
        'Compliance risk refers to the possibility of an organization facing legal penalties, financial losses, or reputational damage due to non-compliance with laws, regulations, industry standards, or internal policies.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const complianceTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Compliance Risk', 'Mitigation Actions'],
    [
      'Regulatory non-compliance',
      '- Stay updated with changing laws, regulations, and industry standards - Establish a dedicated compliance function/team - Conduct regular compliance audits and monitoring',
    ],
    [
      'Weak internal policies and procedures',
      '- Develop and maintain clear compliance policies and SOPs - Regularly review and update internal policies to reflect regulatory changes - Integrate compliance into daily operations',
    ],
    [
      'Lack of employee awareness/training',
      '- Provide regular compliance training and certifications - Conduct awareness campaigns on regulatory obligations - Implement whistleblowing channels and protection',
    ],
    [
      'Data protection and privacy breaches',
      '- Comply with data protection laws (e.g., GDPR, PDPA) - Encrypt sensitive data and apply access controls - Regularly test cybersecurity measures',
    ],
    [
      'Third-party/vendor non-compliance',
      '- Conduct due diligence and compliance checks before onboarding vendors - Include compliance clauses in contracts and SLAs - Monitor and audit vendor performance regularly',
    ],
    [
      'Fraud, bribery, and corruption risk',
      '- Implement strong anti-bribery and corruption (ABC) policies - Enforce segregation of duties and approval limits - Establish confidential reporting and investigation processes',
    ],
    [
      'Reporting and disclosure failures',
      '- Ensure timely and accurate regulatory reporting - Automate reporting processes where possible - Assign accountability for compliance reporting to specific roles',
    ],
    [
      'Reputation damage due to compliance breach',
      '- Develop crisis communication plans - Strengthen stakeholder engagement and transparency - Maintain a culture of ethics and integrity across the organization',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 52;

  return generatePDFTable(
    item,
    [6, 16, 20, 16, 16, 16, 16, 16, 16],
    [firstCol, totalWidth - firstCol],
    25,
    65
  );
};
// #endregion Compliance

// #region Legal
const legalIntroduction = (): PDFText[] => {
  return [
    {
      id: 'legalTitle',
      value: 'Legal Risk',
      x: 25,
      y: 42,
      fontStyle: 'bold',
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: 'legalDesc',
      value:
        'Legal risk refers to the potential for financial loss or harm to an organization due to legal or regulatory actions, including lawsuits, fines, or other penalties.',
      x: 25,
      y: 50,
      maxWidth: 160,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const legalTable = (): PDFText[] => {
  const item: PDFTableItem[][] = generatePrincipalRiskTableData([
    ['Legal Risk', 'Mitigation Actions'],
    [
      'Unclear or poorly drafted contracts',
      '- Use standardized contract templates reviewed by legal experts - Engage qualified legal counsel for contract drafting/review - Ensure contracts clearly define terms, obligations, and dispute resolution mechanisms',
    ],
    [
      'Breach of contract / contractual disputes',
      '- Conduct due diligence on counterparties before agreements - Include arbitration/mediation clauses to manage disputes efficiently - Monitor contract performance and compliance regularly',
    ],
    [
      'Non-compliance with laws and regulations',
      '- Maintain an updated legal register of applicable laws - Appoint a compliance/legal officer to oversee obligations - Conduct regular compliance audits and legal reviews',
    ],
    [
      'Intellectual property (IP) infringement',
      '- Register and protect company trademarks, copyrights, and patents - Monitor for potential IP infringements - Use licensing agreements and IP protection clauses',
    ],
    [
      'Employment-related legal risks',
      '- Develop clear HR policies aligned with labor laws - Provide training on workplace rights, diversity, and anti-discrimination - Document employee contracts and disciplinary actions properly',
    ],
    [
      'Litigation and dispute costs',
      '- Maintain legal contingency reserves and insurance coverage (e.g., liability insurance) - Use alternative dispute resolution (ADR) where possible - Monitor emerging legal risks proactively',
    ],
    [
      'Third-party/vendor legal risks',
      '- Perform legal due diligence on suppliers, partners, and contractors - Include indemnity and compliance clauses in contracts - Audit and monitor third-party legal obligations',
    ],
    [
      'Reputational/legal sanctions',
      '- Establish transparent governance and ethical practices - Maintain open communication with regulators and stakeholders - Implement strong internal controls to prevent misconduct',
    ],
  ]);

  const totalWidth = 160;
  const firstCol = 45;

  return generatePDFTable(
    item,
    [6, 20, 20, 16, 16, 16, 16, 16, 16],
    [firstCol, totalWidth - firstCol],
    25,
    60
  );
};
// #endregion Legal
